import { computed, ref } from 'vue'

import { events$ } from '@/services'

import { setLangTranslations } from '@/plugins/i18n'

import { logInfo } from '@/helpers/Logger'
import { CookieUtil } from '@/utils/Cookie'

import sessionStore from '@/store/session'
import useI18n from '@/hooks/useI18n'

import {
  DEFAULT_DELAY_TIME,
  PACKAGE,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE
} from '@/config/constants'
import { EVENT_LANGUAGE } from '@/config/events'

function merge(target, source) {
  return Object.assign(target, source)
}

// SINGLETON STATE
const globalState = (() => {
  let instance = null

  return {
    getInstance() {
      if (!instance) {
        instance = {
          languageLoaded: ref(false),
          contentIsLoading: ref(true),
        }

        events$.once(EVENT_LANGUAGE.LOADED, () => {
          instance.languageLoaded.value = true
        })
      }
      return instance
    },
  }
})()

const state = globalState.getInstance()
const languageLoaded = state.languageLoaded
const contentIsLoading = state.contentIsLoading

// GLOBAL DATA
const scriptSrcAttr = 'script[src="'

export default function useApplication() {
  // HOOKS
  const { getContent, selectedLanguage, updateHtmlPageData } = useI18n()

  // COMPUTED
  const isContentReady = computed(() => {
    return !contentIsLoading.value
  })

  const isLanguageLoaded = computed(() => {
    return languageLoaded.value
  })

  const isReentryReadyWithoutActiveSession = computed(() => {
    return languageLoaded.value && !sessionStore.isActive.value && !sessionStore.session.leadCode
  })

  const isWww = computed(() => {
    return document.body.dataset.trackApp !== import.meta.env.VITE_APP_ID
  })

  // METHODS
  async function changeLanguage({
    app = window._appName,
    event = EVENT_LANGUAGE.LOADED,
    language = selectedLanguage.value,
    additionalApps = [],
  }) {
    const supportedLanguageValues = SUPPORTED_LANGUAGES.map(s => s.value)
    if (!supportedLanguageValues.includes(language)) {
      language = DEFAULT_LANGUAGE
    }

    let content = {}
    /*
     * @note: app needs to be loaded last, so we can overwrite content on a need-basis.
     */
    const contentFiles = [PACKAGE.ERRORS, PACKAGE.BASIC, PACKAGE.CONTENT, ...additionalApps, app]
    let hasContentChanged = false

    for (const contentFile of contentFiles) {
      const languageChanged = language !== selectedLanguage.value
      const _contentFile = await getContent({ app: contentFile, language, languageChanged })
      if (_contentFile.changed) hasContentChanged = true
      merge(content, _contentFile.content)
    }

    if (hasContentChanged || contentIsLoading.value) {
      setLangTranslations(language, content)

      updateHtmlPageData({ language, app })

      events$.emit(event, { language })

      // Always set loading to false after first language initialization
      contentIsLoading.value = false
    }
  }

  function dispatchCustomEvent(event, params = null) {
    const options = params ? { detail: { ...params } } : null

    logInfo(['%cCustomEvent', 'color: purple', event, options])
    window.dispatchEvent(new CustomEvent(event, options))
  }

  function getPetUrl(language) {
    return import.meta.env.VITE_SANITAS_PET_PATH + '/' + language?.toLowerCase()
  }

  function getRoutingTarget(to) {
    return `/${selectedLanguage.value.toLowerCase()}/` + to
  }

  function hashCode(str) {
    return Array.from(str).reduce((s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0, 0)
  }

  /**
   * Add event listener to konami and clear code for restart application
   *
   * Konami Code uppercase -1341396303
   * Konami Code lowercase -715050255
   * CLEAR-ENTER 1462084291
   * clear-ENTER 618617699
   */
  function initializeClearSequence() {
    let buffer = []
    let lastInput = Date.now()

    document.addEventListener('keydown', event => {
      const current = Date.now()
      if (current - lastInput > 1000) {
        buffer = []
      }

      buffer.push(event.key)
      lastInput = current

      const hasRightLength = buffer.length === 11 || buffer.length === 6
      if (!hasRightLength) {
        return
      }
    })
  }

  function initProgressEvent(setValue) {
    let value = 0
    let progress = 0
    let step = 0.05
    const progressInterval = setInterval(() => {
      value += step
      progress = Math.round((Math.atan(value) / (Math.PI / 2)) * 60 * 1000) / 1000
      setValue(progress)
      if (progress >= 55) {
        clearInterval(progressInterval)
      } else if (progress >= 25) {
        step = 0.01
      }
    }, DEFAULT_DELAY_TIME / 10)
    return progressInterval
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      let shouldAppend = false
      let el = document.querySelector(scriptSrcAttr + src + '"]')
      if (!el) {
        el = document.createElement('script')
        el.type = 'text/javascript'
        el.async = true
        el.src = src
        shouldAppend = true
      } else if (el.hasAttribute('data-loaded')) {
        resolve(el)
        return
      }

      el.addEventListener('error', reject)
      el.addEventListener('abort', reject)
      el.addEventListener('load', function loadScriptHandler() {
        el.setAttribute('data-loaded', true)
        resolve(el)
      })

      if (shouldAppend) document.head.appendChild(el)
    })
  }

  /**
   * @param string selector     Selector about the reference
   * @returns {Promise}
   */
  function pendingHTML(selector) {
    const getter = __selector => {
      if (__selector[0] === '#') {
        return document.getElementById(__selector.substring(1))
      } else if (__selector[0] === '.') {
        return document.getElementsByClassName(__selector.substring(1))
      }
      return document.getSelection(__selector)
    }

    return new Promise((resolve, reject) => {
      const elements = getter(selector)
      if (elements?.id || elements?.length > 0) {
        return resolve(elements)
      }

      let tries = 0
      const poll = setInterval(() => {
        const __elements = getter(selector)
        if (__elements?.id || __elements?.length > 0) {
          clearInterval(poll)
          resolve(__elements)
        }
        tries++
        if (tries > 10) {
          clearInterval(poll)
          reject()
        }
      }, DEFAULT_DELAY_TIME / 5)
    })
  }

  function preferedColorScheme(value) {
    const key = STORAGE_KEY + 'color-scheme'

    if (value) {
      CookieUtil.set({
        key,
        value,
      })
    }

    let colorScheme = CookieUtil.read(key)

    if (!colorScheme) {
      colorScheme = 'light'
    }

    return colorScheme
  }

  function unloadScript(src) {
    return new Promise(function (resolve, reject) {
      const el = document.querySelector(scriptSrcAttr + src + '"]')

      if (!el) {
        reject()
        return
      }

      document.head.removeChild(el)

      resolve()
    })
  }

  return {
    isContentReady,
    isLanguageLoaded,
    isReentryReadyWithoutActiveSession,
    isWww,

    changeLanguage,
    dispatchCustomEvent,
    getPetUrl,
    getRoutingTarget,
    hashCode,
    initializeClearSequence,
    initProgressEvent,
    loadScript,
    pendingHTML,
    preferedColorScheme,
    unloadScript,
  }
}
